import {ChangeEvent, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {agentApi, catalogApi, customersApi, sitesApi} from '../lib/api-resources';
import type {AgentContext, CatalogIngestResponse, Customer, Site} from '../lib/types';
import './WorkbenchPages.css';

function useWorkObject() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [context, setContext] = useState<AgentContext | null>(null);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    customersApi.list({page: 1, page_size: 200, status: 'ACTIVE'}).then((response) => {
      if (!alive) return;
      const items = response.items || [];
      setCustomers(items);
      setCustomerId((current) => current && items.some((item) => item.id === current) ? current : (items[0]?.id || ''));
    }).catch(() => alive && setError('客户资料暂时无法读取，请刷新后重试。'));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!customerId) {
      setSites([]);
      setSiteId('');
      return;
    }
    let alive = true;
    sitesApi.list({page: 1, page_size: 200, customer_id: customerId}).then((response) => {
      if (!alive) return;
      const items = response.items || [];
      setSites(items);
      setSiteId((current) => current && items.some((item) => item.id === current) ? current : (items[0]?.id || ''));
    }).catch(() => alive && setError('网站资料暂时无法读取，请刷新后重试。'));
    return () => { alive = false; };
  }, [customerId]);

  useEffect(() => {
    if (!siteId) {
      setContext(null);
      return;
    }
    let alive = true;
    agentApi.context(siteId).then((value) => alive && setContext(value)).catch(() => alive && setError('网站上下文暂时无法读取，请刷新后重试。'));
    return () => { alive = false; };
  }, [siteId, refreshKey]);

  return {
    customers,
    sites,
    customerId,
    setCustomerId,
    siteId,
    setSiteId,
    context,
    error,
    refresh: () => setRefreshKey((value) => value + 1),
  };
}

function ObjectPicker({object}: {object: ReturnType<typeof useWorkObject>}) {
  return <div className="work-object-picker">
    <label>客户<select value={object.customerId} onChange={(event) => object.setCustomerId(event.target.value)}><option value="">选择客户</option>{object.customers.map((item) => <option key={item.id} value={item.id}>{item.company_name}</option>)}</select></label>
    <label>网站<select value={object.siteId} onChange={(event) => object.setSiteId(event.target.value)}><option value="">选择网站</option>{object.sites.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
  </div>;
}

const MAPPING_FIELDS = [
  {key: 'name', label: '商品名称', multiple: false},
  {key: 'product_id', label: '商品编号（没有可留空）', multiple: false},
  {key: 'sku', label: '规格 / SKU', multiple: false},
  {key: 'images', label: '产品图片', multiple: true},
  {key: 'category', label: '类目', multiple: false},
  {key: 'material', label: '材质', multiple: false},
  {key: 'origin', label: '产地', multiple: false},
  {key: 'packaging', label: '包装与尺寸', multiple: true},
  {key: 'price', label: '价格', multiple: true},
  {key: 'stock', label: '库存', multiple: true},
  {key: 'dispatch', label: '发货时效', multiple: false},
  {key: 'description', label: '备注 / 描述', multiple: false},
] as const;

type MappingValue = string | string[] | null;

function MappingPanel({job, mapping, onChange}: {job: CatalogIngestResponse; mapping: Record<string, MappingValue>; onChange: (key: string, value: MappingValue) => void}) {
  return <div className="catalog-mapping-panel">
    <div className="work-card-kicker">字段确认</div>
    <h3>请确认系统如何理解这份货盘</h3>
    <p className="work-muted">已有字段会自动匹配；不确定的字段可以留空，缺失信息会标记为“需要补充”。</p>
    <div className="catalog-mapping-grid">
      {MAPPING_FIELDS.map((field) => {
        const options = job.detected_fields;
        const selected = mapping[field.key];
        const values = Array.isArray(selected) ? selected : selected ? [selected] : [];
        return <label key={field.key}>
          {field.label}
          <select
            multiple={field.multiple}
            value={field.multiple ? values : (values[0] || '')}
            onChange={(event) => {
              const next = field.multiple
                ? Array.from(event.currentTarget.selectedOptions).map((option) => option.value)
                : (event.currentTarget.value || null);
              onChange(field.key, next);
            }}
          >
            {!field.multiple && <option value="">未映射</option>}
            {options.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
          {field.multiple && <small>按住 Ctrl / Command 可选择多个字段</small>}
        </label>;
      })}
    </div>
  </div>;
}

function RollbackCard({object}: {object: ReturnType<typeof useWorkObject>}) {
  const rollback = object.context?.draft.rollback;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  if (!rollback?.available || !rollback.job_id) return null;

  async function rollbackPublishedVersion() {
    if (!rollback?.job_id || !object.context) return;
    const confirmed = window.confirm(`确定恢复${rollback.target_label}吗？线上版本 v${rollback.current_version ?? '—'} 会暂时退出。`);
    if (!confirmed) return;
    setBusy(true);
    setMessage('');
    try {
      const result = await agentApi.rollback(object.siteId, {
        job_id: rollback.job_id,
        expected_current_version: rollback.current_version || 0,
        confirm: true,
      });
      setMessage(result.message);
      object.refresh();
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '恢复没有完成，请重新读取页面后重试。');
    } finally {
      setBusy(false);
    }
  }

  return <section className="work-card work-rollback-card">
    <div className="work-card-kicker">版本恢复</div>
    <h2>恢复上一个已发布版本</h2>
    <div className="work-gate-grid">
      <div><span>当前线上版本</span><strong>v{rollback.current_version ?? '—'}</strong></div>
      <div><span>恢复目标</span><strong>{rollback.target_label}</strong></div>
    </div>
    <p>{rollback.impact}</p>
    <button className="work-secondary-button" type="button" onClick={rollbackPublishedVersion} disabled={busy}>{busy ? '正在恢复…' : '恢复上一个已发布版本'}</button>
    {message && <p className="work-success-text">{message}</p>}
  </section>;
}

export function CatalogPage() {
  const object = useWorkObject();
  const [file, setFile] = useState<File | null>(null);
  const [job, setJob] = useState<CatalogIngestResponse | null>(null);
  const [mapping, setMapping] = useState<Record<string, MappingValue>>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    setFile(event.target.files?.[0] || null);
    setJob(null);
    setMapping({});
    setNotice('');
    setError('');
  }

  async function upload() {
    if (!object.siteId || !file) {
      setError('请先选择网站和货盘文件。');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await catalogApi.ingest(object.siteId, file);
      setJob(result);
      setMapping({...result.suggested_mapping});
      if (result.status === 'FAILED') setError(result.error_message || '文件没有识别成功，请检查格式后重试。');
      else setNotice(`已识别 ${result.preview_rows.length || result.product_count} 行商品，请确认字段映射。`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '文件识别没有完成，请重试。');
    } finally {
      setBusy(false);
    }
  }

  async function confirmMapping() {
    if (!object.siteId || !job) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await catalogApi.confirm(object.siteId, job.job_id, {mapping});
      setJob(result);
      setNotice(`商品资料已进入产品库：${result.product_count} 个商品、${result.media_count} 张图片。`);
      object.refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '字段确认没有完成，请重试。');
    } finally {
      setBusy(false);
    }
  }

  return <div className="workbench-page page">
    <header className="page-header"><div><div className="work-eyebrow">PRODUCTS & MATERIALS</div><h1>商品与资料</h1><p className="page-subtitle">上传客户已有的货盘，先确认系统识别结果，再让 Agent 使用真实商品建站。</p></div><Link className="work-link-button" to={object.siteId ? '/agent/sites/' + object.siteId : '/agent'}>回到 Agent</Link></header>
    {(object.error || error) && <div className="work-alert">{error || object.error}</div>}
    {notice && <div className="work-success-text work-alert">{notice}</div>}
    <ObjectPicker object={object} />
    <section className="work-grid work-catalog-grid">
      <div className="work-card work-upload-card"><div className="work-card-kicker">货盘入口</div><h2>导入商品资料</h2><p>支持 Excel、CSV、WPS、ZIP 或图片。系统先识别字段，再让你确认映射，不要求你先改成固定模板。</p><label className="work-file-picker"><input type="file" accept=".xlsx,.xls,.csv,.wps,.zip,.jpg,.jpeg,.png,.webp" onChange={chooseFile} /><span>{file?.name || '选择货盘文件'}</span></label><button className="work-primary-button" type="button" onClick={upload} disabled={busy || !file || !object.siteId}>{busy && !job ? '正在识别文件…' : '识别文件字段'}</button><p className="work-muted">原始文件会被保留，系统只复制图片并建立产品库记录。</p></div>
      <div className="work-card"><div className="work-card-kicker">当前产品库</div><h2>{object.context?.customer.name || '尚未选择网站'}</h2><div className="work-stat-row"><div><strong>{object.context?.catalog.product_count ?? '—'}</strong><span>个商品</span></div><div><strong>{object.context?.catalog.media_count ?? '—'}</strong><span>张图片</span></div></div><p>{object.context?.catalog.source || '选择一个网站后，Agent 会读取已确认的产品资料。'}</p><Link className="work-secondary-button" to={object.siteId ? '/agent/sites/' + object.siteId : '/agent'}>让 Agent 使用这些资料</Link></div>
    </section>
    {job?.status === 'MAPPING_REQUIRED' && <section className="work-card"><MappingPanel job={job} mapping={mapping} onChange={(key, value) => setMapping((current) => ({...current, [key]: value}))}/><div className="catalog-preview-block"><h3>识别预览</h3><div className="catalog-preview-table"><div className="catalog-preview-row catalog-preview-heading"><span>商品名称</span><span>类目</span><span>图片</span><span>需要补充</span></div>{job.preview_rows.map((row) => <div className="catalog-preview-row" key={row.source_row}><span>{row.name}</span><span>{row.category}</span><span>{row.image_count}</span><span>{Object.keys(row.missing_fields).length ? Object.keys(row.missing_fields).join('、') : '—'}</span></div>)}</div></div><button className="work-primary-button" type="button" onClick={confirmMapping} disabled={busy}>{busy ? '正在导入产品库…' : '确认映射并导入产品库'}</button></section>}
    {job?.status === 'READY' && <section className="work-card work-success-card"><div className="work-card-kicker">导入完成</div><h2>真实商品资料已准备好</h2><p>{job.product_count} 个商品和 {job.media_count} 张图片已经进入产品库，Agent 下一次读取网站时会使用这份资料。</p><p className="work-muted">来源文件：{job.original_filename}</p></section>}
    <section className="work-card work-boundary-card"><div className="work-card-kicker">导入后会发生什么</div><h2>你会在每一步看到结果</h2><div className="work-flow"><span>上传文件</span><i>→</i><span>识别字段</span><i>→</i><span>确认映射</span><i>→</i><span>商品、规格和图片进入产品库</span><i>→</i><span>Agent 使用真实商品</span></div><p className="work-muted">标准化只发生在系统内部；客户不需要先按固定 Excel 模板整理。</p></section>
  </div>;
}

export function PreviewVersionsPage() {
  const object = useWorkObject();
  const draft = object.context?.draft;
  return <div className="workbench-page page">
    <header className="page-header"><div><div className="work-eyebrow">PREVIEW & VERSIONS</div><h1>预览与版本</h1><p className="page-subtitle">集中查看草稿、视觉审查、预览和已经发布的版本。</p></div><Link className="work-link-button" to={object.siteId ? '/agent/sites/' + object.siteId : '/agent'}>进入建站工作台</Link></header>
    {object.error && <div className="work-alert">{object.error}</div>}<ObjectPicker object={object} />
    <section className="work-grid"><div className="work-card"><div className="work-card-kicker">当前草稿</div><h2>{object.context?.site.name || '选择一个网站'}</h2><div className="work-stat-row"><div><strong>{draft?.draft_revision ?? '—'}</strong><span>草稿修订</span></div><div><strong>{draft?.page_count ?? '—'}</strong><span>页面</span></div><div><strong>{draft?.section_count ?? '—'}</strong><span>区块</span></div></div><p>{draft?.has_draft ? '草稿已存在，可以回到 Agent 继续生成、修改和审查。' : '还没有草稿，先让 Agent 读取资料并生成方案。'}</p></div><div className="work-card"><div className="work-card-kicker">当前状态</div><h2>{draft?.published ? `线上版本 v${draft.published_version ?? '—'}` : '尚未发布当前版本'}</h2><ul className="work-check-list"><li>{draft?.current_version ? '已保存当前版本' : '等待保存版本'}</li><li>{draft?.approval?.approved ? '版本已批准' : '等待视觉审查和批准'}</li><li>{draft?.preview_confirmed ? '预览已确认' : '等待确认预览'}</li><li>{draft?.published ? '网站已发布' : '发布入口尚未解锁'}</li></ul><p className="work-muted">每一步的结果来自网站记录，重新进入页面后仍会保留。</p></div></section>
    <RollbackCard object={object} />
  </div>;
}

export function PublishPage() {
  const object = useWorkObject();
  const draft = object.context?.draft;
  return <div className="workbench-page page">
    <header className="page-header"><div><div className="work-eyebrow">PUBLISH</div><h1>发布</h1><p className="page-subtitle">发布只接受通过预览、确认和安全切换检查的版本。</p></div><Link className="work-link-button" to={object.siteId ? '/agent/sites/' + object.siteId : '/agent'}>回到 Agent</Link></header>
    {object.error && <div className="work-alert">{object.error}</div>}<ObjectPicker object={object} />
    <section className="work-card work-publish-card"><div className="work-card-kicker">发布准备情况</div><h2>{object.context?.site.name || '选择一个网站后查看发布准备情况'}</h2><div className="work-gate-grid"><div><span>内容草稿</span><strong>{draft?.has_draft ? '已准备' : '未准备'}</strong></div><div><span>可恢复版本</span><strong>{draft?.rollback?.available ? '已保存' : '待发布后生成'}</strong></div><div><span>商品资料</span><strong>{object.context?.catalog.product_count ? '已读取' : '待补充'}</strong></div><div><span>网站状态</span><strong>{draft?.published ? `线上 v${draft.published_version ?? '—'}` : '等待确认预览'}</strong></div></div><p className="work-muted">你可以在 Agent 工作台完成生成、修改、预览和发布；这里显示结果，不绕过安全发布流程。</p><Link className="work-primary-button" to={object.siteId ? '/agent/sites/' + object.siteId : '/agent'}>在建站工作台完成准备</Link></section>
    <RollbackCard object={object} />
  </div>;
}

export function AdvancedSettingsPage() {
  return <div className="workbench-page page">
    <header className="page-header"><div><div className="work-eyebrow">ADVANCED SETTINGS</div><h1>高级设置</h1><p className="page-subtitle">服务器、托管、域名、TLS 和调试工具集中放在这里，普通建站流程不需要进入。</p></div><Link className="work-link-button" to="/agent">回到 Agent</Link></header>
    <section className="work-grid"><div className="work-card"><div className="work-card-kicker">基础资料</div><h2>客户与网站注册表</h2><p>管理客户档案、网站名称、域名和语言等全局配置。</p><div className="work-link-list"><Link to="/customers">客户资料 →</Link><Link to="/sites">网站设置 →</Link></div></div><div className="work-card"><div className="work-card-kicker">运维与系统</div><h2>基础设施工具</h2><p>这些信息由系统和运维人员管理，普通用户只在需要时查看结果。</p><div className="work-link-list"><Link to="/servers">服务器与托管 →</Link><Link to="/templates">模板资源 →</Link><Link to="/dashboard">系统概览 →</Link></div></div></section>
  </div>;
}
